"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeConfig = exports.createApptTimeMachine = exports.machineJSON = exports.findMatchingAppt = void 0;
const xstate_1 = require("xstate");
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const States_1 = require("../../lib/constants/States");
const Actions_1 = require("../../lib/constants/Actions");
const utils_1 = require("../../lib/utils");
const api_1 = require("../../lib/api");
const Facets_1 = require("../../lib/constants/Facets");
const ProviderLocations_utils_1 = require("../../components/ProviderLocations/ProviderLocations.utils");
const WeekDayFilter_1 = require("../../components/ApptTime/WeekDayFilter");
const TimeFilter_1 = require("../../components/ApptTime/TimeFilter");
const validation_1 = require("../../lib/constants/validation");
const utils_directBook_1 = require("../../components/DirectBook/utils.directBook");
/**
 * @param {Object} appt - an appointment returned from the `scheduling api`
 * @return {String}
 */
const keyFromAppointment = (appt) => [
    appt.start_datetime,
    appt.duration_minutes,
    appt.provider_id,
    appt.location_id
].join(':');
/**
 * Merge two lists of appointments and returns a list of
 * unique appointments
 * @param {Array} appointments
 * @param {Array} nextAppointments
 * @return {Array}
 */
const mergeAppointments = (appointments, nextAppointments) => {
    const existing = new Set(appointments.map(keyFromAppointment));
    const uniq = nextAppointments.filter((appt) => !existing.has(keyFromAppointment(appt)));
    return appointments.concat(uniq);
};
/**
 * Returns params used for the scheduling api call
 * @param {Object} ctx - machine context
 * @return {Object}
 */
const schedulingParamsFromCtx = (ctx, ev) => ({
    provider: ctx.provider,
    location_ids: ctx.query.locationIds,
    period_start: ev.periodStart || ctx.query.periodStart || ctx.jumpedToDate || new Date(),
    purpose: ctx.query.purpose.name,
    patient_relationship: ctx.query.patientRel,
    role: ctx.query.role,
    visibility: ctx.query.visibility
});
/**
 * Returns a matching appointment based on ctx.locationId and ctx.periodStart
 * @param {Object} ctx - machine context
 * @param {Object} ev - event triggered by the completion of the availability/scheduling api call
 * @param {Array} ev.data - array of appointments from the availability/scheduling api call
 * @return {Object} - a matching appointment, if one found
 * @return {Null} - if no matching appointment is found
 */
const findMatchingAppt = (ctx, ev) => {
    const { locationId, periodStart } = ctx;
    const appointments = ev.data.schedRes;
    return (appointments.find(({ location_id, start_datetime }) => location_id.toString() === locationId && start_datetime === periodStart) || null);
};
exports.findMatchingAppt = findMatchingAppt;
const actions = {
    clearFilters: (0, xstate_1.assign)({
        filterBy: () => ({
            timeOfDay: new Set(),
            dayOfWeek: new Set()
        })
    }),
    clearAllFilters: (0, xstate_1.assign)({
        filterBy: () => ({
            timeOfDay: new Set(),
            dayOfWeek: new Set()
        }),
        filterCount: 0,
        jumpedToDate: null,
        selectedLocationIds: (ctx) => { var _a, _b, _c; return new Set((_c = (_b = (_a = ctx.locsByAvailability) === null || _a === void 0 ? void 0 : _a.bookable) === null || _b === void 0 ? void 0 : _b.map((loc) => loc.id)) !== null && _c !== void 0 ? _c : []); },
        query: (ctx) => {
            var _a, _b, _c;
            return (Object.assign(Object.assign({}, ctx.query), { locationIds: (_c = (_b = (_a = ctx.locsByAvailability) === null || _a === void 0 ? void 0 : _a.bookable) === null || _b === void 0 ? void 0 : _b.map((loc) => loc.id)) !== null && _c !== void 0 ? _c : [], timeOfDay: new Set(), dayOfWeek: new Set(), periodStart: null }));
        }
    }),
    setfilterBy: (0, xstate_1.assign)({
        filterBy: (ctx, ev) => (Object.assign(Object.assign({}, ctx.filterBy), ev.data))
    }),
    updateQuery: (0, xstate_1.assign)({
        query: (ctx, ev) => {
            const hasLocationChanged = ev.data && ev.data.currentId;
            let periodStart = ev.data.periodStart || ctx.query.periodStart || null;
            if (hasLocationChanged) {
                periodStart = ctx.jumpedToDate;
            }
            return Object.assign(Object.assign(Object.assign({}, ctx.query), ev.data), { periodStart, 
                // toggle location id from locationIds ctx based on clicked current location ID
                locationIds: Array.from((0, ProviderLocations_utils_1.toggleLocationInSet)(ctx.query.locationIds, ev.data.currentId)) });
        },
        // why? reset locationId when updated
        // we use this id to be checked when render step 2
        // and display booking time slots based on that
        // it should be reset when user clicks on any other location
        locationId: () => null,
        // updateQuery is called for every subsequent data load after inital
        isInitialDataLoadFromPreviousStep: false
    }),
    setFilterCount: (0, xstate_1.assign)({
        filterCount: (ctx, ev) => {
            var _a, _b, _c;
            const { locationIds, periodStart, dayOfWeek, timeOfDay } = ev.data;
            let filterCount = 0;
            if (locationIds.length !== ((_c = (_b = (_a = ctx.locsByAvailability) === null || _a === void 0 ? void 0 : _a.bookable) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0)) {
                filterCount++;
            }
            if (periodStart) {
                filterCount++;
            }
            if (dayOfWeek.size > 0 && dayOfWeek.size !== WeekDayFilter_1.DAYS_OF_WEEK.length) {
                filterCount++;
            }
            if (timeOfDay.size > 0 &&
                timeOfDay.size !== Object.keys(TimeFilter_1.TIME_OF_DAY).length) {
                filterCount++;
            }
            return filterCount;
        }
    }),
    setAppointments: (0, xstate_1.assign)({
        appointments: (ctx, ev) => ev.data.schedRes,
        lastApptQueriedFor: (ctx, ev) => ev.data.lastApptQueriedFor
    }),
    addAppointments: (0, xstate_1.assign)((ctx, ev) => {
        // Merge because of how the api time windowing works.
        // We may get an appointment at the head of new list that is the same
        // as the last appointment from the current list
        const mergedAppointments = mergeAppointments(ctx.appointments, ev.data.schedRes);
        let firstNewAppointmentId;
        if (mergedAppointments.length > ctx.appointments.length) {
            firstNewAppointmentId = (0, utils_1.getApptSlotId)(mergedAppointments[ctx.appointments.length]);
        }
        return {
            firstNewAppointmentId,
            appointments: mergedAppointments,
            lastApptQueriedFor: ev.data.lastApptQueriedFor
        };
    }),
    selectAppointment: (0, xstate_1.assign)({
        selectedAppointment: (ctx, ev) => ev.data.appointment
    }),
    selectAppointmentFromList: (0, xstate_1.assign)({
        selectedAppointment: (ctx, ev) => (0, exports.findMatchingAppt)(ctx, ev)
    }),
    setIsCaptchaVerified: (0, xstate_1.assign)((ctx, ev) => {
        return {
            isCaptchaVerified: ev.data
        };
    }),
    sendParentNext: (0, xstate_1.sendParent)((ctx) => {
        const { selectedAppointment } = ctx;
        return {
            type: Actions_1.NEXT,
            data: {
                selectedAppointment
            }
        };
    }),
    sendParentCaptchaVerified: (0, xstate_1.sendParent)((ctx) => {
        const { isCaptchaVerified } = ctx;
        return {
            type: Actions_1.CAPTCHA_VERIFIED,
            data: { isCaptchaVerified }
        };
    }),
    setLocsByAvailability: (0, xstate_1.assign)({
        locsByAvailability: (ctx, ev) => {
            const { provider } = ctx;
            // Add null safety checks for malformed API responses
            if (!ev.data || !ev.data.facets || !Array.isArray(ev.data.facets)) {
                return [];
            }
            const { facets } = ev.data;
            const { terms = [] } = facets.find(({ field }) => field === Facets_1.LOCATION_ID) || {};
            const availableLocs = terms.reduce((acc, { value: locId, count }) => {
                if (count > 0) {
                    acc.push(locId);
                }
                return acc;
            }, []);
            const locsByAvailability = {
                bookable: [],
                nonBookable: []
            };
            provider.locations.forEach((location) => {
                if (availableLocs.includes(location.id)) {
                    locsByAvailability.bookable.push(location);
                }
                else {
                    locsByAvailability.nonBookable.push(location);
                }
            });
            return locsByAvailability;
        }
    }),
    setQueryAvailableLocationIds: (0, xstate_1.assign)({
        query: (ctx) => {
            const { locsByAvailability, locationId, selectedLocationIds } = ctx;
            // update locationIds in ctx on first load of apptTime step
            const bookableLocations = locsByAvailability === null || locsByAvailability === void 0 ? void 0 : locsByAvailability.bookable;
            if (!bookableLocations || !Array.isArray(bookableLocations)) {
                return Object.assign(Object.assign({}, ctx.query), { locationIds: [] });
            }
            let locationIds = bookableLocations
                .map((loc) => `${loc.id}`)
                .includes(locationId)
                ? [parseInt(locationId, 10)]
                : bookableLocations.map((loc) => loc.id);
            // If there already are selectedLocationIds, make sure we de-select
            if (selectedLocationIds.size) {
                locationIds = locationIds.filter((el) => selectedLocationIds.has(el));
            }
            return Object.assign(Object.assign({}, ctx.query), { locationIds });
        }
    }),
    setJumpedToDate: (0, xstate_1.assign)({
        jumpedToDate: (ctx, ev) => {
            return ev.data.periodStart;
        }
    }),
    setAltProviders: (0, xstate_1.assign)({
        altProviders: (_ctx, ev) => {
            return ev.data;
        }
    }),
    setCareTeamsProviders: (0, xstate_1.assign)({
        careTeamsProviders: (_ctx, ev) => {
            return ev.data;
        },
        // Computed property to reduce component-level logic duplication
        shouldShowCareTeamsInSidebar: (ctx, ev) => {
            const careTeamsData = ev.data;
            return ((0, utils_directBook_1.isCareTeamsForStep2Enabled)(ctx.featureFlags) &&
                careTeamsData &&
                Array.isArray(careTeamsData) &&
                careTeamsData.length > 0);
        }
    }),
    /**
     * Reset deep linking location params when the location filters are interacted with
     */
    sendParentResetLocationFilter: (0, xstate_1.sendParent)((_ctx) => {
        return {
            type: Actions_1.RESET_LOCATION_FILTER
        };
    }),
    clearFiltersOnParent: (0, xstate_1.sendParent)((_, { type }) => {
        const data = {
            timeOfDay: new Set(),
            dayOfWeek: new Set()
        };
        // Sent from CollapsibleFilters. Can't clear these fields otherwise.
        if (type === Actions_1.CLEAR_ALL_FILTERS) {
            Object.assign(data, {
                selectedLocationIds: new Set(),
                jumpedToDate: ''
            });
        }
        return {
            type: Actions_1.SET_FILTERS_ON_PARENT,
            data
        };
    }),
    // NOTE this should always be called last because it is expecting the context to be updated already
    setFiltersOnParent: (0, xstate_1.sendParent)(({ filterBy, query, jumpedToDate }) => {
        return {
            type: Actions_1.SET_FILTERS_ON_PARENT,
            data: Object.assign(Object.assign({}, filterBy), { selectedLocationIds: new Set(query.locationIds || []), jumpedToDate })
        };
    }),
    setEmptyAppointments: (0, xstate_1.assign)({
        appointments: () => []
    })
};
const guards = {
    /* no results if no appoinment data comes back OR we have no bookable locations (slots mismatch) */
    hasNoResults: (ctx, ev) => !ev.data ||
        (0, isEmpty_1.default)(ev.data) ||
        ev.data.length === 0 ||
        ctx.locsByAvailability.bookable.length === 0,
    periodStartAndLocationIdInCtxIsAvailable: (ctx, ev) => !!(0, exports.findMatchingAppt)(ctx, ev),
    /* locationId exists in context only when deepLinked*/
    periodStartUnavailableAndLocationIdExistsInCtx: (ctx, ev) => {
        const { locationId, periodStart } = ctx;
        if (locationId && periodStart) {
            const appointments = ev.data.schedRes;
            return !appointments.some(({ location_id, start_datetime }) => location_id.toString() === locationId &&
                start_datetime === periodStart);
        }
        return false;
    },
    isCaptchaNotVerifiedCtx: (ctx) => !ctx.isCaptchaVerified,
    isCaptchaNotVerifiedSession: (_ctx, ev) => {
        return !ev.data;
    },
    hasAppointments: (ctx) => ctx.appointments && ctx.appointments.length > 0,
    isCareTeamsEnabled: (ctx) => (0, utils_directBook_1.isCareTeamsForStep2Enabled)(ctx === null || ctx === void 0 ? void 0 : ctx.featureFlags)
};
const services = {
    getAppointments: (ctx, ev) => {
        const { customerCode, productName, apiBaseURL } = ctx;
        const api = new api_1.Api({ customerCode, productName, apiBaseURL });
        /** if deepLinking or jumpToDate,
         *  call /scheduling/list directly with ctx.query.periodStart as start_datetime */
        if (ctx.query.periodStart && ev.type !== Actions_1.LOAD_MORE) {
            return api.getScheduling(schedulingParamsFromCtx(ctx, ev));
        }
        /** if loading the page from the previous step or loading more results,
         *  call /availability/next then use next availability start_datetime to call /scheduling/list */
        return api.getNextSchedulingList({
            params: schedulingParamsFromCtx(ctx, ev),
            isInitialLoad: ctx.isInitialDataLoadFromPreviousStep
        });
    },
    getAvailableLocs(ctx) {
        const { provider, query: { patientRel, purpose }, customerCode, productName, visibility, apiBaseURL } = ctx;
        const api = new api_1.Api({ customerCode, productName, apiBaseURL });
        return api.getAvailableLocsForProvider(provider.id, {
            patientRel,
            purpose: purpose.name
        }, visibility.role);
    },
    checkCaptchaVerified: (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const { customerCode, productName, apiBaseURL } = ctx;
        const api = new api_1.Api({ customerCode, productName, apiBaseURL });
        const res = yield api.checkCaptcha();
        return res.verified;
    }),
    getAltProviders: (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const { customerCode, productName, provider, locations, services, query, apiBaseURL } = ctx;
        const queryParams = {
            patientRel: query.patientRel,
            hasAvailability: true,
            purpose: query.purpose.name
        };
        const { getAltProviders } = services;
        let filteredLocations = locations;
        const urlParams = new URLSearchParams(window.location.search);
        const searchCoordinates = decodeURIComponent(urlParams.get('search_coordinates') || '');
        // Check if all available locations are selected (indicating no user filter)
        const isAllLocationsSelected = ((_a = query.locationIds) === null || _a === void 0 ? void 0 : _a.length) === ((_c = (_b = ctx.locsByAvailability) === null || _b === void 0 ? void 0 : _b.bookable) === null || _c === void 0 ? void 0 : _c.length);
        // If we have search coordinates and no specific location filter, use coordinates
        if (searchCoordinates &&
            isAllLocationsSelected &&
            validation_1.LAT_LON_REGEX.test(searchCoordinates)) {
            const [lat, lon] = searchCoordinates.split(',');
            filteredLocations = [
                {
                    coordinates: {
                        lat: parseFloat(lat),
                        lon: parseFloat(lon)
                    }
                }
            ];
        }
        // Otherwise use selected location(s)
        else if (((_d = query.locationIds) === null || _d === void 0 ? void 0 : _d.length) > 0) {
            filteredLocations = locations.filter((location) => query.locationIds.includes(location.id));
        }
        const api = new api_1.Api({ customerCode, productName, apiBaseURL });
        return getAltProviders
            ? yield api.getAltProvidersData(getAltProviders, provider, filteredLocations, queryParams)
            : null;
    }),
    getCareTeamsProviders: (ctx) => {
        const { services: { getCareTeamsProviders }, provider, query } = ctx;
        const queryParams = {
            has_availability: true,
            // TODO: review after getting this reviewed
            // filter: {
            //   or: {
            //     'provider.care_team': careTeams
            //   },
            //   not: {
            //     'provider.id': [provider.id]
            //   },
            //   and: {
            //     and: {
            //       'provider.appointment_ehr_purposes.name': [query.purpose.name],
            //       'provider.appointment_ehr_purposes.patient_relationship': [
            //         query.patientRel
            //       ],
            //       'provider.care_team': provider.care_team
            //     }
            //   }
            // },
            slots_filter: {
                and: {
                    and: {
                        purpose: query.purpose.name
                    }
                }
            }
        };
        if (getCareTeamsProviders) {
            return getCareTeamsProviders(provider, queryParams);
        }
        return null;
    }
};
// This is exposed for snapshot testing
const machineJSON = ({ provider, locations, appointmentQuery, periodStart, locationId, isCaptchaVerified, customerCode, csrfToken, visibility, services, productName, featureFlags, apptTimeFilters = {}, darkshipFeatureFlags, apiBaseURL }) => {
    // fitlers from a previous instance
    const { selectedLocationIds, timeOfDay, dayOfWeek, jumpedToDate } = apptTimeFilters;
    return {
        id: `Machine__${States_1.apptTime}`,
        initial: States_1.loadingAvailableLocs,
        context: {
            altProviders: [],
            careTeamsProviders: [],
            shouldShowCareTeamsInSidebar: false,
            provider,
            locations,
            appointments: [],
            selectedAppointment: null,
            selectedLocationIds: new Set(selectedLocationIds),
            filterBy: {
                timeOfDay: new Set(timeOfDay),
                dayOfWeek: new Set(dayOfWeek)
            },
            query: {
                periodStart,
                patientRel: appointmentQuery.patientRel,
                purpose: appointmentQuery.purpose,
                locationIds: null,
                visibility: [visibility.schedulingVisibility],
                role: visibility.role
            },
            locsByAvailability: {
                bookable: [],
                nonBookable: []
            },
            locationId,
            periodStart,
            isCaptchaVerified,
            customerCode,
            csrfToken,
            visibility,
            /** date jumped to by the user; to be displayed in the UI */
            jumpedToDate: jumpedToDate || '',
            /** Flag to indicate inital load of scheduling data for the page when user is navigating through the flow naturally (not deep linking, loading more results, or jumping to a date).
             *  If deep linking, `periodStart` will be initially defined and `isInitialDataLoadFromPreviousStep` will be false.
             *  If loading more results, the `updateQuery` action will be called which sets `isInitialDataLoadFromPreviousStep` to false. */
            isInitialDataLoadFromPreviousStep: Boolean(periodStart) === true ? false : true,
            services,
            productName,
            filterCount: 0,
            featureFlags,
            darkshipFeatureFlags,
            apiBaseURL
        },
        on: {
            [Actions_1.UPDATE_APPT_QUERY]: {
                actions: ['updateQuery', 'setFiltersOnParent'],
                target: States_1.fetchingAltProviders
            },
            [Actions_1.JUMP_TO_DATE]: {
                actions: ['updateQuery', 'setJumpedToDate', 'setFiltersOnParent'],
                target: States_1.jumpToDate
            },
            [Actions_1.SET_ALL_FILTERS]: {
                actions: [
                    'updateQuery',
                    'setJumpedToDate',
                    'setfilterBy',
                    'setFiltersOnParent',
                    'setFilterCount'
                ],
                target: States_1.jumpToDate
            },
            [Actions_1.CLEAR_ALL_FILTERS]: {
                actions: ['clearAllFilters', 'clearFiltersOnParent'],
                target: States_1.fetchingAltProviders
            },
            [Actions_1.RESET_LOCATION_FILTER]: {
                actions: ['sendParentResetLocationFilter']
            }
        },
        states: {
            [States_1.fetchingAltProviders]: {
                type: 'parallel',
                states: {
                    [States_1.altProviders]: {
                        initial: States_1.pending,
                        states: {
                            [States_1.pending]: {
                                invoke: {
                                    src: 'getAltProviders',
                                    onDone: {
                                        actions: ['setAltProviders'],
                                        target: States_1.done
                                    },
                                    onError: States_1.done
                                }
                            },
                            [States_1.done]: {
                                type: 'final'
                            }
                        }
                    },
                    [States_1.careTeamProviders]: {
                        initial: States_1.routing,
                        states: {
                            [States_1.routing]: {
                                always: [
                                    {
                                        cond: 'isCareTeamsEnabled',
                                        target: States_1.pending
                                    },
                                    {
                                        target: States_1.done
                                    }
                                ]
                            },
                            [States_1.pending]: {
                                invoke: {
                                    src: 'getCareTeamsProviders',
                                    onDone: {
                                        actions: ['setCareTeamsProviders'],
                                        target: States_1.done
                                    },
                                    onError: States_1.done
                                }
                            },
                            [States_1.done]: {
                                type: 'final'
                            }
                        }
                    }
                },
                onDone: States_1.newSearch
            },
            [States_1.loadingAvailableLocs]: {
                initial: States_1.pending,
                states: {
                    [States_1.pending]: {
                        invoke: {
                            src: 'getAvailableLocs',
                            onDone: {
                                actions: [
                                    'setLocsByAvailability',
                                    'setQueryAvailableLocationIds'
                                ],
                                target: States_1.done
                            },
                            onError: States_1.loadingAvailableLocsError
                        }
                    },
                    [States_1.done]: {
                        type: 'final'
                    },
                    [States_1.loadingAvailableLocsError]: {}
                },
                onDone: States_1.fetchingAltProviders
            },
            [States_1.newSearch]: {
                initial: States_1.routing,
                on: {
                    [Actions_1.SELECT_APPOINTMENT]: [
                        {
                            actions: ['selectAppointment', 'sendParentNext'],
                            target: States_1.done
                        }
                    ]
                },
                states: {
                    [States_1.routing]: {
                        always: [
                            {
                                cond: 'isCaptchaNotVerifiedCtx',
                                target: States_1.checkingCaptchaSession
                            },
                            {
                                target: States_1.pending
                            }
                        ]
                    },
                    [States_1.checkingCaptchaSession]: {
                        invoke: {
                            id: 'checkingCaptchaVerified',
                            src: 'checkCaptchaVerified',
                            onDone: [
                                {
                                    cond: 'isCaptchaNotVerifiedSession',
                                    target: States_1.verifyingCaptcha
                                },
                                {
                                    actions: [
                                        'setIsCaptchaVerified',
                                        'sendParentCaptchaVerified'
                                    ],
                                    target: States_1.pending
                                }
                            ],
                            onError: States_1.verifyingCaptcha
                        }
                    },
                    [States_1.verifyingCaptcha]: {
                        on: {
                            [Actions_1.CAPTCHA_CANCELLED]: {
                                target: States_1.routing
                            },
                            [Actions_1.CAPTCHA_VERIFIED]: [
                                {
                                    actions: [
                                        'setIsCaptchaVerified',
                                        'sendParentCaptchaVerified'
                                    ],
                                    target: States_1.pending
                                }
                            ],
                            [Actions_1.CAPTCHA_ERROR]: {
                                target: States_1.verifyingCaptchaError
                            }
                        }
                    },
                    [States_1.verifyingCaptchaError]: {
                        on: {
                            [Actions_1.CAPTCHA_RESET]: {
                                target: States_1.checkingCaptchaSession
                            },
                            [Actions_1.CAPTCHA_CANCELLED]: {
                                target: States_1.routing
                            }
                        }
                    },
                    [States_1.success]: {
                        on: {
                            [Actions_1.LOAD_MORE]: {
                                target: States_1.done,
                                actions: 'updateQuery'
                            },
                            [Actions_1.CLEAR_FILTERS]: {
                                actions: ['clearFilters', 'clearFiltersOnParent']
                            },
                            [Actions_1.SET_LOCAL_FILTER]: {
                                actions: ['setfilterBy', 'setFiltersOnParent']
                            }
                        }
                    },
                    [States_1.deepLinkingTimeSlotUnavailableError]: {
                        on: {
                            [Actions_1.LOAD_MORE]: {
                                target: States_1.done,
                                actions: 'updateQuery'
                            },
                            [Actions_1.CLEAR_FILTERS]: {
                                actions: ['clearFilters', 'clearFiltersOnParent']
                            },
                            [Actions_1.SET_LOCAL_FILTER]: {
                                actions: ['setfilterBy', 'setFiltersOnParent']
                            }
                        }
                    },
                    [States_1.noResults]: {},
                    [States_1.pending]: {
                        invoke: {
                            id: 'getAppointments.newSearch',
                            src: 'getAppointments',
                            onDone: [
                                { cond: 'hasNoResults', target: States_1.noResults },
                                {
                                    cond: 'periodStartAndLocationIdInCtxIsAvailable',
                                    actions: ['selectAppointmentFromList', 'sendParentNext']
                                },
                                {
                                    cond: 'periodStartUnavailableAndLocationIdExistsInCtx',
                                    actions: 'setAppointments',
                                    target: States_1.deepLinkingTimeSlotUnavailableError
                                },
                                { actions: 'setAppointments', target: States_1.success }
                            ],
                            onError: States_1.noResults
                        }
                    },
                    [States_1.done]: {
                        type: 'final'
                    }
                },
                onDone: States_1.nextSearch
            },
            [States_1.jumpToDate]: {
                initial: States_1.pending,
                on: {
                    [Actions_1.SELECT_APPOINTMENT]: [
                        {
                            actions: ['selectAppointment', 'sendParentNext'],
                            target: States_1.done
                        }
                    ]
                },
                states: {
                    [States_1.pending]: {
                        type: 'parallel',
                        states: {
                            appointments: {
                                initial: 'fetching',
                                states: {
                                    fetching: {
                                        invoke: {
                                            id: 'getAppointments.newSearch',
                                            src: 'getAppointments',
                                            onDone: [
                                                {
                                                    cond: 'hasNoResults',
                                                    target: States_1.done,
                                                    actions: ['setEmptyAppointments']
                                                },
                                                { actions: 'setAppointments', target: States_1.done }
                                            ],
                                            onError: {
                                                target: States_1.done,
                                                actions: ['setEmptyAppointments']
                                            }
                                        }
                                    },
                                    [States_1.done]: {
                                        type: 'final'
                                    }
                                }
                            },
                            altProviders: {
                                initial: 'fetching',
                                states: {
                                    fetching: {
                                        invoke: {
                                            src: 'getAltProviders',
                                            onDone: {
                                                actions: ['setAltProviders'],
                                                target: States_1.done
                                            },
                                            onError: States_1.done
                                        }
                                    },
                                    [States_1.done]: {
                                        type: 'final'
                                    }
                                }
                            }
                        },
                        onDone: [
                            { cond: 'hasAppointments', target: States_1.success },
                            {
                                target: States_1.noResults
                            }
                        ]
                    },
                    [States_1.success]: {
                        id: 'success',
                        on: {
                            [Actions_1.LOAD_MORE]: {
                                target: States_1.done,
                                actions: 'updateQuery'
                            },
                            [Actions_1.CLEAR_FILTERS]: {
                                actions: ['clearFilters', 'clearFiltersOnParent']
                            },
                            [Actions_1.SET_LOCAL_FILTER]: {
                                actions: ['setfilterBy', 'setFiltersOnParent']
                            }
                        }
                    },
                    // NOTE:
                    // we're in a `noResults` substate of the `jumpToDate` state
                    // which means we already have appointments loaded on the page,
                    // but then "jumped" to a date with no results.
                    [States_1.noResults]: {
                        id: 'noResults',
                        on: {
                            [Actions_1.CLEAR_FILTERS]: {
                                actions: ['clearFilters', 'clearFiltersOnParent']
                            },
                            [Actions_1.SET_LOCAL_FILTER]: {
                                actions: ['setfilterBy', 'setFiltersOnParent']
                            }
                        }
                    },
                    [States_1.done]: {
                        type: 'final'
                    }
                },
                onDone: States_1.nextSearch
            },
            [States_1.nextSearch]: {
                initial: States_1.pending,
                on: {
                    [Actions_1.SELECT_APPOINTMENT]: [
                        {
                            actions: ['selectAppointment', 'sendParentNext'],
                            target: States_1.done
                        }
                    ]
                },
                states: {
                    [States_1.success]: {
                        on: {
                            [Actions_1.LOAD_MORE]: {
                                target: States_1.pending,
                                actions: 'updateQuery'
                            },
                            [Actions_1.CLEAR_FILTERS]: {
                                actions: ['clearFilters', 'clearFiltersOnParent']
                            },
                            [Actions_1.SET_LOCAL_FILTER]: {
                                actions: ['setfilterBy', 'setFiltersOnParent']
                            }
                        }
                    },
                    // NOTE:
                    // we're in a `noResults` from the `nextSearch` state
                    // which means we're on page 2 or greater of the list.
                    // So there are appointments already loaded on the page
                    [States_1.noResults]: {
                        on: {
                            [Actions_1.CLEAR_FILTERS]: {
                                actions: ['clearFilters', 'clearFiltersOnParent']
                            },
                            [Actions_1.SET_LOCAL_FILTER]: {
                                actions: ['setfilterBy', 'setFiltersOnParent']
                            }
                        }
                    },
                    [States_1.pending]: {
                        invoke: {
                            id: 'getAppointments.nextSearch',
                            src: 'getAppointments',
                            onDone: [
                                {
                                    cond: 'hasNoResults',
                                    target: States_1.noResults
                                },
                                { actions: 'addAppointments', target: States_1.success }
                            ],
                            onError: States_1.noResults
                        }
                    }
                }
            },
            [States_1.done]: {
                type: 'final'
            }
        }
    };
};
exports.machineJSON = machineJSON;
const createApptTimeMachine = (_a) => {
    var { config = (0, exports.makeConfig)() } = _a, props = __rest(_a, ["config"]);
    return (0, xstate_1.Machine)((0, exports.machineJSON)(props)).withConfig(config);
};
exports.createApptTimeMachine = createApptTimeMachine;
const makeConfig = () => ({ services, actions, guards });
exports.makeConfig = makeConfig;
//# sourceMappingURL=apptTime.js.map